/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/tenancy.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


export interface CurrentUser {
  /** The token's `sub` — the same string the audit chain records as the actor. */
  readonly subject: string;
  /** Falls back to the subject when there is no local credential row. */
  readonly display_name: string;
  /**
   * What this tenant calls itself, or `null` when no registry row records a name. **This is the
   * tenant's name and deliberately not its id.** `tenant_id` is absent from this response on hard
   * rule 1: the tenant comes from a validated claim at the edge, and a wire type that declares the
   * id invites a client to plumb it back into a request. A name addresses nothing — no route
   * accepts one and no policy is keyed on one — so publishing it costs the guarantee nothing and
   * lets a screen say whose estate is being read, which on a pooled deployment is the first thing
   * an operator checks. `null` is a real answer rather than a defensive default: an OIDC
   * deployment can present a valid claim for a tenant that has no `platform.tenant` row, and a
   * client must render the absence rather than treating it as a failed read. Required-and-nullable
   * rather than optional, so a client is made to handle the case instead of finding the key
   * missing.
   */
  readonly organisation_name: string | null;
  /** Role names held in this tenant. Empty means none, which denies everything. */
  readonly roles: readonly string[];
  /**
   * The union of every held role's permissions, as `action:resource_type`, sorted. Empty means
   * "may do nothing yet" rather than a failed read. Use it to hide controls, never as the only
   * check — the server authorizes every route regardless.
   */
  readonly permissions: readonly string[];
}

export function isCurrentUser(value: unknown): value is CurrentUser {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['subject'] === 'string' &&
    typeof candidate['display_name'] === 'string' &&
    (typeof candidate['organisation_name'] === 'string' || candidate['organisation_name'] === null) &&
    Array.isArray(candidate['roles']) &&
    (candidate['roles'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['permissions']) &&
    (candidate['permissions'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

export interface TenantUser {
  readonly subject: string;
  readonly display_name: string;
  readonly roles: readonly string[];
  /**
   * True means they cannot log in again. It does **not** mean their current session has ended —
   * see the status operation. Kept alongside `status` rather than replaced by it, because it is
   * the field an admin *sets*: `PUT /users/{subject}/disabled` writes this, and `status` is
   * derived from it. A client should render `status` and act on `disabled`.
   */
  readonly disabled: boolean;
  /**
   * **Derived from the stored columns, never a column of its own.** `disabled` wins; otherwise an
   * account with no password has an invitation outstanding and one with a password is active. A
   * stored status column would be a second answer to "can this account be used", and the two would
   * drift — `core.tenancy`'s provisioning state is derived from rows for the same stated reason.
   * Deriving it also means every account that existed before invitations were introduced reports
   * correctly with no backfill: they all have a password, so they are `active` or `disabled`.
   * `pending_invitation` means the person has been created and has never set a password, so **no
   * credential exists that could authenticate them**. It is not a weaker kind of active: sign-in
   * refuses them with the same sentence an unknown address gets.
   */
  readonly status: 'pending_invitation' | 'active' | 'disabled';
  /**
   * **A separate axis from `status`, not a fourth member of it.** An invitation proves the address
   * when it is opened and the password is set afterwards, so `active` implies this; an account
   * created before invitations may be active and unverified, and one may be verified while still
   * `pending_invitation` if the link was opened and the form abandoned. Folding it into the status
   * enum would make those two states unrepresentable.
   */
  readonly email_verified: boolean;
  readonly created_at: string;
  /**
   * `null` means never signed in. Not an epoch and not an empty string: never-arrived and dormant
   * are different facts and a client must render the absence as words.
   */
  readonly last_login_at: string | null;
}

export function isTenantUser(value: unknown): value is TenantUser {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['subject'] === 'string' &&
    typeof candidate['display_name'] === 'string' &&
    Array.isArray(candidate['roles']) &&
    (candidate['roles'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['disabled'] === 'boolean' &&
    (['pending_invitation', 'active', 'disabled'] as readonly unknown[]).includes(candidate['status']) &&
    typeof candidate['email_verified'] === 'boolean' &&
    typeof candidate['created_at'] === 'string' &&
    (typeof candidate['last_login_at'] === 'string' || candidate['last_login_at'] === null)
  );
}

export interface TenantUserPage {
  readonly items: readonly TenantUser[];
  readonly meta: Readonly<Record<string, unknown>>;
}

export function isTenantUserPage(value: unknown): value is TenantUserPage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isTenantUser(item)) &&
    candidate['meta'] !== undefined
  );
}

export interface Role {
  readonly name: string;
  readonly description: string;
  /**
   * What this tenant's row actually stores, as `action:resource_type` — not the current definition
   * of the starter role. See the operation description.
   */
  readonly permissions: readonly string[];
  /** How many subjects hold it. A count rather than the list, which is per user. */
  readonly holders: number;
  /**
   * One of the four roles provisioning creates. A starter role can have drifted from its current
   * definition, and this is what lets a client explain why.
   */
  readonly is_starter: boolean;
}

export function isRole(value: unknown): value is Role {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['name'] === 'string' &&
    typeof candidate['description'] === 'string' &&
    Array.isArray(candidate['permissions']) &&
    (candidate['permissions'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['holders'] === 'number' &&
    typeof candidate['is_starter'] === 'boolean'
  );
}

export interface RoleList {
  readonly items: readonly Role[];
}

export function isRoleList(value: unknown): value is RoleList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isRole(item))
  );
}

export interface PermissionGroup {
  readonly resource_type: string;
  readonly actions: readonly string[];
}

export function isPermissionGroup(value: unknown): value is PermissionGroup {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['resource_type'] === 'string' &&
    Array.isArray(candidate['actions']) &&
    (candidate['actions'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

export interface PermissionCatalogue {
  readonly items: readonly PermissionGroup[];
}

export function isPermissionCatalogue(value: unknown): value is PermissionCatalogue {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isPermissionGroup(item))
  );
}

export interface PasswordResetRequest {
  /**
   * The account to reset. Never used as a delivery address — the server looks the credential up
   * and mails the stored subject, so a caller cannot have a reset link delivered anywhere they
   * choose.
   */
  readonly email: string;
}

export function isPasswordResetRequest(value: unknown): value is PasswordResetRequest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['email'] === 'string'
  );
}

export interface PasswordResetAccepted {
  /**
   * Always `true`. Present so the body is a shape rather than an empty object, and deliberately
   * **not** a statement that an account was found — see the operation.
   */
  readonly accepted: boolean;
  /**
   * A property of this deployment, not of the account. `none` means no mailer is configured, so
   * nothing was delivered to anybody; a client should say so rather than telling the person to
   * check their inbox.
   */
  readonly transport: 'email' | 'none';
}

export function isPasswordResetAccepted(value: unknown): value is PasswordResetAccepted {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['accepted'] === 'boolean' &&
    (['email', 'none'] as readonly unknown[]).includes(candidate['transport'])
  );
}

export interface PasswordResetCompletion {
  /**
   * The value from the emailed link. Only its SHA-256 digest is stored, so this exact string is
   * the only thing that can redeem the reset and the platform cannot reconstruct it from the row.
   */
  readonly token: string;
  /**
   * Held to the same `validate_password_strength` checks as signup — the length floor plus the
   * rules a floor cannot express. The email is known here (it is the credential the token names),
   * so the derived-from-your-own-address check applies; the organisation name is not, so that one
   * does not.
   */
  readonly password: string;
}

export function isPasswordResetCompletion(value: unknown): value is PasswordResetCompletion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['token'] === 'string' &&
    typeof candidate['password'] === 'string'
  );
}

export interface AddUserRequest {
  readonly email: string;
  readonly display_name?: string;
  /**
   * One of this tenant's starter roles. Defaults to `reader`, the least privileged — an admin must
   * deliberately choose to grant more.
   */
  readonly role_name?: string;
}

export function isAddUserRequest(value: unknown): value is AddUserRequest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['email'] === 'string'
  );
}

export interface AddUserResponse {
  /**
   * `true` when this address belonged to an account of this tenant that had been retired, and that
   * account was brought back rather than a new one created. **The fact an administrator acts on,
   * and one they cannot infer from anything else on this response.** A restored account keeps its
   * `created_at`, its last recorded sign-in and everything the audit chain holds against the
   * subject — which is correct, because it is the same person — and it keeps **none** of the role
   * bindings it held before: those were revoked when it was retired, so only the role named in
   * this request is in force. Somebody retired as an `admin` and re-added as a `reader` comes back
   * as a `reader`. An administrator who is not told treats a restore as a fresh account, and the
   * two differ in what the person's history says about them. A screen that says "added" for a
   * restore is a true-sounding sentence about the wrong event. **`false` does not mean the address
   * was never used**, and the distinction is the reason the 409 below still exists: an address
   * retired in a *different* tenant is still refused, because reviving it would move a credential
   * between tenancies while the first tenant's audit chain goes on naming that subject. This field
   * answers what happened *here*. Always present rather than only when `true`, for
   * `must_change_password`'s reason: a client treating its absence as `false` would keep reporting
   * every restore as a fresh account the day this route stopped sending it.
   */
  readonly restored: boolean;
  readonly subject: string;
  readonly role_name: string;
  /**
   * The state the account starts in. **Always `pending_invitation`**, and reported rather than
   * left to be inferred from the absence of a sign-in: an admin screen can say "invited" instead
   * of showing a new colleague as though they were already using the product. A one-member enum
   * today and a field rather than a sentence baked into a client, for the reason the removed
   * `must_change_password` gives in the same words: a client that had hardcoded "they have been
   * invited" would keep saying it if this route ever started creating accounts some other way. It
   * is the same vocabulary as `TenantUser.status`, so a screen reads one set of values.
   */
  readonly status: 'pending_invitation';
  /**
   * Whether the invitation was actually sent, and therefore whether the administrator still has to
   * pass the activation link on themselves. `sent` — the message was accepted by whichever
   * transport this deployment uses. `failed` — a transport is configured and refused or was
   * unreachable; the account exists, an invitation is outstanding, and nobody has been told.
   * `none` — this deployment has no mail transport at all, so nothing was attempted; the same
   * spelling as `transport: "none"` on the password-reset route, deliberately, so a client does
   * not have to learn two words for "nothing was delivered". **Three values rather than a
   * boolean**, because `failed` and `none` are fixed by different people in different places — one
   * is a transport outage or a wrong credential, the other is an operator who has configured no
   * transport at all — and one value covering both would send an administrator to ask about the
   * wrong one. **This replaced `welcome_email`, and the difference is what the message
   * contained.** That field reported whether a *password* had been delivered; nothing in this flow
   * mails one, so the question it answered no longer exists. What an administrator now has to
   * chase is a link, and the recovery is `POST /v1/auth/users/{subject}/invitation` rather than
   * reading a password off their own screen and passing it on. No reason string and no host: an
   * administrator cannot act on either, and a transport's own message routinely carries the
   * recipient, the relay host or the sender mailbox.
   */
  readonly invitation_email: 'sent' | 'failed' | 'none';
  /**
   * How long the invitation is good for, so a screen can say so rather than guessing. 48 today —
   * longer than the one-hour reset and verification windows, because this one waits on somebody
   * starting a job rather than on somebody who has just clicked something. A field rather than a
   * constant in a client for the reason above: a client that had baked in "48 hours" would keep
   * saying it after an operator's policy changed. **`must_change_password` is gone from this
   * response**, and its absence is the feature. It was always `true` because an administrator
   * chose the password and mailed it, so from the write onward two people held it. There is no
   * password here to force a change of — the invitee chooses the only one the account will ever
   * have. Migration 0081's column and the claim it drives both remain, for the accounts created
   * under the old flow.
   */
  readonly expires_in_hours: number;
}

export function isAddUserResponse(value: unknown): value is AddUserResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['subject'] === 'string' &&
    typeof candidate['role_name'] === 'string' &&
    (['pending_invitation'] as readonly unknown[]).includes(candidate['status']) &&
    (['sent', 'failed', 'none'] as readonly unknown[]).includes(candidate['invitation_email']) &&
    typeof candidate['expires_in_hours'] === 'number' &&
    typeof candidate['restored'] === 'boolean'
  );
}

/** What a successful signup returns. **Deliberately not a token** — see `signUp`. */
export interface SignupAccepted {
  /**
   * `true` in the ordinary case: the account is unverified and a link has been mailed. `false`
   * when the address already held a proof from the pre-signup code flow, in which case the account
   * is verified already and the person may log in immediately.
   */
  readonly verification_required: boolean;
  /**
   * The normalised address the link was sent to, echoed so a screen can render it without
   * re-reading the form — and because the normalisation may differ from what was typed.
   */
  readonly email: string;
  /**
   * Whether a message actually left this deployment. `false` means the account exists and no mail
   * transport is configured, so an operator must verify the address by other means — reported
   * rather than hidden, because "check your email" over an undelivered message is the failure this
   * field exists to prevent.
   */
  readonly delivered: boolean;
  /** How long the mailed link stays redeemable. */
  readonly expires_in_minutes: number;
}

export function isSignupAccepted(value: unknown): value is SignupAccepted {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['verification_required'] === 'boolean' &&
    typeof candidate['email'] === 'string' &&
    typeof candidate['delivered'] === 'boolean' &&
    typeof candidate['expires_in_minutes'] === 'number'
  );
}

export interface EmailVerificationConfirm {
  /**
   * The value from the link's query string. Never logged and never stored — only its digest is
   * persisted, so this is the one place the raw token exists outside the mail.
   */
  readonly token: string;
}

export function isEmailVerificationConfirm(value: unknown): value is EmailVerificationConfirm {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['token'] === 'string'
  );
}

export interface EmailVerificationResult {
  /**
   * Always `true` on a 200. A failure is the 422 rather than a 200 carrying `false`, so a client
   * cannot treat an unverified account as verified by forgetting to read a flag.
   */
  readonly verified: boolean;
  /**
   * `true` when this token had already been redeemed and the account was verified — a double-click
   * or a prefetching mail client. The account is in the same state either way.
   */
  readonly already_verified: boolean;
}

export function isEmailVerificationResult(value: unknown): value is EmailVerificationResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['verified'] === 'boolean' &&
    typeof candidate['already_verified'] === 'boolean'
  );
}
